/**
 * Modal — the centred dialog behind every form, detail and confirmation overlay,
 * plus `useDismissable`, the one hook that gives the whole overlay kit the same
 * behaviour contract: Escape closes the topmost layer, Tab is trapped inside the
 * panel, focus returns to whatever opened it, and body scroll is locked.
 *
 * The hook lives in this file (not a hooks folder) because Modal is the canonical
 * overlay; Drawer, Popover and Dropdown import it from here so there is exactly one
 * implementation of the dismiss contract. It keeps a module-level layer stack so a
 * Dropdown opened inside a Modal swallows Escape before the Modal sees it, and a
 * reference-counted scroll lock so nested overlays restore the body exactly once.
 */
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/* -------------------------------------------------------------------------- */
/* useDismissable                                                             */
/* -------------------------------------------------------------------------- */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Tabbable descendants of `container`, in document order, skipping hidden nodes. */
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.tabIndex !== -1 &&
      element.getClientRects().length > 0,
  );
}

/** Ids of the currently open layers; the last entry owns Escape. */
const layerStack: string[] = [];

let scrollLockCount = 0;
let restoreOverflow = '';
let restorePaddingRight = '';

function lockBodyScroll(): void {
  if (scrollLockCount === 0) {
    const { body } = document;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    restoreOverflow = body.style.overflow;
    restorePaddingRight = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      // Add to whatever padding the layout already has, so the page does not jump
      // left by the width of the scrollbar it just lost.
      const existing = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${existing + scrollbarWidth}px`;
    }
  }
  scrollLockCount += 1;
}

function unlockBodyScroll(): void {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = restoreOverflow;
    document.body.style.paddingRight = restorePaddingRight;
  }
}

export interface UseDismissableOptions {
  /** Whether the overlay is currently rendered. */
  isOpen: boolean;
  /** Called for Escape and, when enabled, for an outside pointer press. */
  onClose: () => void;
  /** The element holding the overlay content — and, for anchored surfaces, its trigger. */
  ref: RefObject<HTMLElement | null>;
  /** Keep Tab inside `ref`. Off for menus, which move focus themselves. */
  trapFocus?: boolean;
  /** Freeze page scrolling behind the overlay. Off for anchored surfaces. */
  lockScroll?: boolean;
  /** Close when a pointer press lands outside `ref`. */
  closeOnOutsideClick?: boolean;
  /** On open, focus `[data-autofocus]`, else the first tabbable node, else the panel. */
  autoFocus?: boolean;
  /** Return focus to the element that was focused before opening. */
  restoreFocus?: boolean;
}

/**
 * The shared overlay behaviour: Escape, focus trap, focus restore and scroll lock.
 * Defaults are tuned for a modal dialog; anchored surfaces opt out of the parts
 * they do not want. Focus is never restored when the overlay closed because the
 * user pressed something else — that press owns the focus.
 */
export function useDismissable({
  isOpen,
  onClose,
  ref,
  trapFocus = true,
  lockScroll = true,
  closeOnOutsideClick = false,
  autoFocus = true,
  restoreFocus = true,
}: UseDismissableOptions): void {
  const layerId = useId();
  const onCloseRef = useRef(onClose);
  const shouldRestoreRef = useRef(restoreFocus);

  // Keep the latest handler without re-running the subscription effect.
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const node = ref.current;
    if (!isOpen || !node) return;

    layerStack.push(layerId);
    shouldRestoreRef.current = restoreFocus;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (lockScroll) lockBodyScroll();

    if (autoFocus) {
      const preferred = node.querySelector<HTMLElement>('[data-autofocus]:not([disabled])');
      (preferred ?? getFocusable(node)[0] ?? node).focus();
    }

    const isTopLayer = () => layerStack[layerStack.length - 1] === layerId;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!isTopLayer()) return;
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !trapFocus || !isTopLayer()) return;

      const focusable = getFocusable(node);
      if (focusable.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && node.contains(active);

      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };

    const handlePointerDown = (event: Event) => {
      if (!isTopLayer()) return;
      const target = event.target;
      if (target instanceof Node && node.contains(target)) return;
      shouldRestoreRef.current = false;
      onCloseRef.current();
    };

    // Keys listen on the bubble phase so a descendant that legitimately owns
    // Escape — an open Tooltip, a combobox clearing its query — can stop the event
    // before the layer sees it. Pointer presses listen on capture, where nothing
    // inside can suppress the outside-click dismissal.
    document.addEventListener('keydown', handleKeyDown);
    if (closeOnOutsideClick) {
      document.addEventListener('pointerdown', handlePointerDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown, true);

      const index = layerStack.lastIndexOf(layerId);
      if (index !== -1) layerStack.splice(index, 1);

      if (lockScroll) unlockBodyScroll();

      if (shouldRestoreRef.current && previouslyFocused?.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, layerId, ref, trapFocus, lockScroll, closeOnOutsideClick, autoFocus, restoreFocus]);
}

/* -------------------------------------------------------------------------- */
/* Modal                                                                      */
/* -------------------------------------------------------------------------- */

const modalPanel = cva(
  [
    'relative z-10 flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden',
    'rounded-xl border border-border bg-surface shadow-popover animate-scale-in',
  ],
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible name of the dialog; also rendered as the header heading. */
  title: ReactNode;
  /** Supporting line under the title, wired up as `aria-describedby`. */
  description?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Convenience slot rendered inside a `ModalFooter`. */
  footer?: ReactNode;
  children: ReactNode;
  closeOnBackdrop?: boolean;
  /** Hide the header dismiss button — for dialogs that demand an explicit choice. */
  hideCloseButton?: boolean;
  closeLabel?: string;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    isOpen,
    onClose,
    title,
    description,
    size = 'md',
    footer,
    children,
    closeOnBackdrop = true,
    hideCloseButton = false,
    closeLabel = 'Close dialog',
    className,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  forwardedRef,
) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const descriptionId = useId();

  useDismissable({ isOpen, onClose, ref: panelRef });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 sm:p-6">
      <div
        aria-hidden="true"
        onClick={closeOnBackdrop ? onClose : undefined}
        className="fixed inset-0 bg-overlay/60 backdrop-blur-sm animate-fade-in"
      />
      <div
        ref={(node) => {
          panelRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={description ? descriptionId : ariaDescribedBy}
        tabIndex={-1}
        className={cn(modalPanel({ size }), className)}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 id={headingId} className="text-base font-semibold text-content">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-sm text-content-muted">
                {description}
              </p>
            ) : null}
          </div>
          {hideCloseButton ? null : (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="-mr-1.5 -mt-1 shrink-0 rounded-lg p-1.5 text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="scrollbar-thin flex-1 overflow-y-auto p-5">{children}</div>

        {footer ? <ModalFooter>{footer}</ModalFooter> : null}
      </div>
    </div>,
    document.body,
  );
});

export const ModalFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function ModalFooter({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col-reverse gap-2 border-t border-border bg-surface-sunken/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-end',
          className,
        )}
        {...props}
      />
    );
  },
);
